import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2, Hourglass, Bell, DoorOpen, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COPY = {
  waiting: { icon: Hourglass, title: "You're on the waitlist", body: "We'll email you when you're next in line, and again when a spot opens." },
  next_in_line: { icon: Bell, title: "You're next in line!", body: "We'll email you the moment a spot opens. Keep an eye on your inbox." },
  spot_open: { icon: DoorOpen, title: 'A spot is open for you!', body: 'You have 7 days to enroll before the spot is released to the next family. Contact us to claim it.' },
  enrolled: { icon: CheckCircle2, title: "You're enrolled!", body: 'Welcome to Little Splash. We can’t wait to see you in the pool.' },
  released: { icon: Hourglass, title: 'This spot was released', body: 'Reach out to us any time and we’ll help you find the next opening.' },
  removed: { icon: Hourglass, title: 'No longer on the waitlist', body: 'Reach out to us any time if you’d like to rejoin.' },
};

export default function WaitlistStatus() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    const load = () => fetch(`${API}/waitlist/status/${token}`).then((r) => (r.ok ? r.json() : Promise.reject())).then(setData).catch(() => setErr('We couldn’t find that waitlist spot.'));
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [token]);

  if (err) return <main className="py-24 text-center text-splash-green" data-testid="waitlist-status-error">{err}</main>;
  if (!data) return <main className="py-24 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-splash-teal" /></main>;
  const c = COPY[data.status] || COPY.waiting;
  const Icon = c.icon;
  return (
    <main className="py-16 md:py-24" data-testid="waitlist-status-page">
      <div className="max-w-xl mx-auto px-6 text-center">
        <div className="uppercase tracking-widest text-splash-coral text-xs font-semibold">{data.program_label}</div>
        <h1 className="mt-3 font-display font-bold text-4xl sm:text-5xl text-splash-green">Hi {data.parent_first_name}!</h1>
        {data.position && (
          <div className="mt-8 mx-auto w-44 h-44 rounded-full bg-splash-green text-white grid place-items-center shadow-xl" data-testid="waitlist-status-position">
            <div>
              <div className="text-xs uppercase tracking-widest text-splash-cream/70">Your spot</div>
              <div className="font-display font-bold text-6xl">#{data.position}</div>
              <div className="text-xs text-splash-cream/70">of {data.total_waiting}</div>
            </div>
          </div>
        )}
        <div className="mt-8 rounded-3xl bg-white border border-splash-sand p-6 text-left flex gap-4" data-testid="waitlist-status-card">
          <Icon className="w-7 h-7 text-splash-coral shrink-0" />
          <div>
            <div className="font-display font-bold text-xl text-splash-green" data-testid="waitlist-status-title">{c.title}</div>
            <p className="mt-1 text-splash-green/80">{c.body}</p>
            {data.status === 'spot_open' && data.offer_expires_at && (
              <p className="mt-2 font-semibold text-splash-coralDark">Enroll by {new Date(data.offer_expires_at).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}</p>
            )}
          </div>
        </div>
        <p className="mt-6 text-sm text-splash-green/70">While you wait, book a swim assessment or join a First Splash class to get to know our pool and coaches.</p>
        <div className="mt-4 flex flex-wrap justify-center gap-3">
          <Link to="/contact" className="rounded-full bg-splash-coral text-white px-6 py-3 font-semibold" data-testid="waitlist-status-contact">Contact us</Link>
          <Link to="/book?type=assessment" className="rounded-full border border-splash-sand bg-white text-splash-green px-6 py-3 font-semibold" data-testid="waitlist-status-assessment">Book a swim assessment</Link>
        </div>
        <p className="mt-6 text-xs text-splash-green/50">Bookmark this page — it updates live.</p>
      </div>
    </main>
  );
}
